import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  adminListPromoPurchases,
  adminApprovePromoPurchase,
  adminRejectPromoPurchase,
} from "../../api/api";
import normalizeMediaUrl from "../../utils/media";

/**
 * Small UI helpers
 */
function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label ? <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label> : null}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Badge({ children, color = "#1f2937", bg = "#e5e7eb" }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        fontSize: 12,
        borderRadius: 999,
        color,
        background: bg,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function isMonthlyPurchase(row) {
  return String(row?.package?.type || "").toUpperCase() === "MONTHLY";
}

function formatMoney(value) {
  const n = Number(value || 0);
  return `₹${n.toFixed(2)}`;
}

function getMonthlyBoxes(row) {
  if (!Array.isArray(row?.boxes_json)) return [];
  return row.boxes_json
    .map((box) => parseInt(box, 10))
    .filter((box) => Number.isFinite(box) && box > 0)
    .sort((a, b) => a - b);
}

function sppSeasonLabel(row) {
  if (!isMonthlyPurchase(row)) return "";
  const n = parseInt(row?.package_number, 10);
  return n > 0 ? `SPP ${n}` : "-";
}

function sppMonthsLabel(row) {
  const boxes = getMonthlyBoxes(row);
  return boxes.length ? boxes.join(", ") : "-";
}
function extractUTR(remarks = "") {
  const s = String(remarks || "");
  // UTRs are typically 10-16 alphanumeric (often 12). Pick first good candidate.
  const m = s.toUpperCase().match(/\b([A-Z0-9]{10,16})\b/);
  return m ? m[1] : "";
}

export default function AdminPromoPurchases({
  title = "Promo Purchases",
  description = "Approve or reject consumer promo purchases. This view is kept intentionally simple.",
  defaultStatus = "PENDING",
  readOnly = false,
} = {}) {
  const location = useLocation();
  const normalizedDefaultStatus = ["PENDING", "APPROVED", "REJECTED", "CANCELLED"].includes(String(defaultStatus || "").toUpperCase())
    ? String(defaultStatus || "").toUpperCase()
    : "PENDING";
  const [status, setStatus] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const s = String(params.get("status") || normalizedDefaultStatus).toUpperCase();
      return ["PENDING", "APPROVED", "REJECTED", "CANCELLED"].includes(s) ? s : normalizedDefaultStatus;
    } catch {
      return normalizedDefaultStatus;
    }
  });
  const [kind, setKind] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const k = String(params.get("kind") || "").toLowerCase();
      return ["150", "750", "759", "monthly"].includes(k) ? k : "";
    } catch {
      return "";
    }
  });
  const [userId, setUserId] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const v = params.get("user_id");
      return v && /^\d+$/.test(v) ? v : "";
    } catch {
      return "";
    }
  });
  const [dateFrom, setDateFrom] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return params.get("date_from") || "";
    } catch {
      return "";
    }
  });
  const [dateTo, setDateTo] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return params.get("date_to") || "";
    } catch {
      return "";
    }
  });

  const [triAppSlug, setTriAppSlug] = useState(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return (params.get("tri_app_slug") || "").trim();
    } catch {
      return "";
    }
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const nextStatus = String(params.get("status") || normalizedDefaultStatus).toUpperCase();
      const nextKind = String(params.get("kind") || "").toLowerCase();
      const nextUserId = params.get("user_id") || "";

      setStatus(["PENDING", "APPROVED", "REJECTED", "CANCELLED"].includes(nextStatus) ? nextStatus : normalizedDefaultStatus);
      setKind(["150", "750", "759", "monthly"].includes(nextKind) ? nextKind : "");
      setTriAppSlug((params.get("tri_app_slug") || "").trim());
      setUserId(nextUserId && /^\d+$/.test(nextUserId) ? nextUserId : "");
      setDateFrom(params.get("date_from") || "");
      setDateTo(params.get("date_to") || "");
    } catch {
      setStatus(normalizedDefaultStatus);
      setKind("");
      setTriAppSlug("");
      setUserId("");
      setDateFrom("");
      setDateTo("");
    }
  }, [location.search, normalizedDefaultStatus]);

  const statusOptions = useMemo(
    () => [
      { value: "PENDING", label: "Pending" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
      { value: "CANCELLED", label: "Cancelled" },
    ],
    []
  );

  const kindOptions = useMemo(
    () => [
      { value: "", label: "Any kind" },
      { value: "150", label: "Prime 150" },
      { value: "750", label: "Prime 750" },
      { value: "759", label: "Monthly SPP (legacy filter)" },
      { value: "monthly", label: "Monthly (all)" },
    ],
    []
  );

  useEffect(() => {
    const onResize = () => {
      try {
        setIsMobile(window.innerWidth < 1024); // lg breakpoint
      } catch {
        setIsMobile(false);
      }
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  async function fetchRows() {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (status) params.status = status;
      if (kind) params.kind = kind;
      if (triAppSlug) params.tri_app_slug = triAppSlug;
      if (userId) params.user_id = userId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const items = await adminListPromoPurchases(params);
      setRows(Array.isArray(items) ? items : []);
    } catch (e) {
      const d = e?.response?.data;
      setErr(d?.detail || "Failed to load promo purchases");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRows();
  }, [status, kind, triAppSlug, userId, dateFrom, dateTo]);

  async function handleApprove(r) {
    if (!window.confirm(`Approve purchase #${r.id}?`)) return;
    try {
      await adminApprovePromoPurchase(r.id);
      await fetchRows();
      alert(`Purchase #${r.id} approved successfully.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to approve");
    }
  }

  async function handleReject(r) {
    const reason = window.prompt("Enter reject reason (optional):", "");
    if (reason === null) return;
    try {
      await adminRejectPromoPurchase(r.id, reason || "");
      await fetchRows();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reject");
    }
  }

  function renderStatusBadge(s) {
    switch (String(s || "").toUpperCase()) {
      case "PENDING":
        return <Badge color="#b45309" bg="#ffedd5">Pending</Badge>;
      case "APPROVED":
        return <Badge color="#065f46" bg="#d1fae5">Approved</Badge>;
      case "REJECTED":
        return <Badge color="#991b1b" bg="#fee2e2">Rejected</Badge>;
      case "CANCELLED":
        return <Badge color="#374151" bg="#e5e7eb">Cancelled</Badge>;
      default:
        return <Badge>{s}</Badge>;
    }
  }

  function PaymentCell({ proofUrl, utr, remarks }) {
    const url = normalizeMediaUrl(proofUrl || "");
    const guessedUtr = utr || extractUTR(remarks || "");
    const isImg = isImageUrl(url);

    return (
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        {url ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>View Proof</a>
            {isImg ? (
              <a href={url} target="_blank" rel="noreferrer" style={{ display: "inline-block" }}>
                <img
                  src={url}
                  alt="payment-proof"
                  style={{
                    display: "block",
                    width: 96,
                    height: 64,
                    objectFit: "cover",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
              </a>
            ) : null}
          </div>
        ) : (
          <span style={{ color: "#64748b", fontSize: 12 }}>No file</span>
        )}
        <div style={{ fontSize: 12, color: "#0f172a" }}>
          <div><span style={{ color: "#64748b" }}>UTR:</span> {guessedUtr || ""}</div>
          {remarks ? (
            <div style={{ color: "#475569", whiteSpace: "pre-wrap", marginTop: 2, maxWidth: 260, wordBreak: "break-word" }}>
              {remarks}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  function PackageCell({ row }) {
    const pkg = row.package || {};
    const monthly = isMonthlyPurchase(row);
    const pkgName = monthly ? "Monthly SPP" : (pkg.name || pkg.code || "");
    const unitPrice = Number(pkg.price || 0);

    return (
      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{pkgName}</div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {formatMoney(unitPrice)}
        </div>
        {row.tri_app_slug ? (
          <div style={{ fontSize: 12, color: "#475569" }}>
            TRI: {row.tri_app_slug}{row.tri_product_id ? ` • Product #${row.tri_product_id}` : ""}
          </div>
        ) : null}
        {row.selected_product_name ? (
          <div style={{ fontSize: 12, color: "#475569" }}>
            Selected: {row.selected_product_name}
          </div>
        ) : null}
      </div>
    );
  }

  function DesktopTable() {
    return (
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          overflowX: "auto",
          overflowY: "hidden",
          background: "#fff",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "70px 160px 160px 120px 100px 140px 140px 180px 100px 140px 90px 60px 120px 120px 140px 240px 140px",
            gap: 8,
            padding: "10px",
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            fontWeight: 700,
            color: "#0f172a",
            minWidth: 1880,
          }}
        >
          <div>ID</div>
          <div>User</div>
          <div>Sponsor</div>
          <div>Role</div>
          <div>Pincode</div>
          <div>City</div>
          <div>State</div>
          <div>Package</div>
          <div>SPP Season</div>
          <div>Months</div>
          <div>Type</div>
          <div>Qty</div>
          <div>Amount</div>
          <div>Status</div>
          <div>Requested</div>
          <div>Payment</div>
          <div>Actions</div>
        </div>

        {/* Rows */}
        <div>
          {rows.map((r) => {
            const pkg = r.package || {};
            const requestedAt = r.requested_at ? new Date(r.requested_at).toLocaleString() : "";
            const proofUrl = r.payment_proof || "";
            const isMonthly = isMonthlyPurchase(r);
            const userLabel = r.user_username ? `${r.user_username} ${r.user_id ? `(#${r.user_id})` : ""}` : (r.user_id ? `#${r.user_id}` : "");
            const sponsorLabel = r.sponsor_username ? r.sponsor_username : (r.sponsor_user_id ? `#${r.sponsor_user_id}` : (r.sponsor_code || ""));
            const amountPaid = Number(r.amount_paid || 0);

            return (
              <div
                key={r.id}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "70px 160px 160px 120px 100px 140px 140px 180px 100px 140px 90px 60px 120px 120px 140px 240px 140px",
                  gap: 8,
                  padding: "10px",
                  borderBottom: "1px solid #e2e8f0",
                  alignItems: "center",
                  minWidth: 1880,
                }}
              >
                <div>#{r.id}</div>
                <div style={{ wordBreak: "break-all" }}>
                  <div>{userLabel}</div>
                  {r.user_full_name ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>{r.user_full_name}</div>
                  ) : null}
                </div>

                <div style={{ wordBreak: "break-all" }}>
                  <div>{sponsorLabel || ""}</div>
                  {r.sponsor_full_name ? (
                    <div style={{ fontSize: 12, color: "#64748b" }}>{r.sponsor_full_name}</div>
                  ) : null}
                </div>

                <div>{r.user_role_label || ""}</div>
                <div>{r.user_pincode || ""}</div>
                <div>{r.user_city_name || ""}</div>
                <div>{r.user_state_name || ""}</div>

                <PackageCell row={r} />
                <div style={{ fontWeight: 700 }}>{isMonthly ? sppSeasonLabel(r) : "-"}</div>
                <div>{isMonthly ? sppMonthsLabel(r) : "-"}</div>

                <div>{pkg.type || ""}</div>
                <div>{r.quantity ?? 1}</div>
                <div>{formatMoney(amountPaid)}</div>
                <div>{renderStatusBadge(r.status)}</div>
                <div style={{ fontSize: 12 }}>{requestedAt}</div>

                <div>
                  <PaymentCell proofUrl={proofUrl} utr={r.utr} remarks={r.remarks} />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {!readOnly && String(r.status || "").toUpperCase() === "PENDING" ? (
                    <>
                      <button
                        onClick={() => handleApprove(r)}
                        style={{
                          padding: "6px 10px",
                          background: "#059669",
                          color: "#fff",
                          border: 0,
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(r)}
                        style={{
                          padding: "6px 10px",
                          background: "#ef4444",
                          color: "#fff",
                          border: 0,
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span style={{ color: "#64748b", fontSize: 12 }}>No actions</span>
                  )}
                </div>
              </div>
            );
          })}
                  {!loading && rows.length === 0 ? (
            <div style={{ padding: 12, color: "#64748b" }}>No results</div>
          ) : null}
        </div>
      </div>
    );
  }

  function MobileCards() {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r) => {
          const pkg = r.package || {};
          const requestedAt = r.requested_at ? new Date(r.requested_at).toLocaleString() : "";
          const proofUrl = r.payment_proof || "";
          const isMonthly = isMonthlyPurchase(r);
          const userLabel = r.user_username ? `${r.user_username} ${r.user_id ? `(#${r.user_id})` : ""}` : (r.user_id ? `#${r.user_id}` : "");
          const sponsorLabel = r.sponsor_username ? r.sponsor_username : (r.sponsor_user_id ? `#${r.sponsor_user_id}` : (r.sponsor_code || ""));
          const pkgName = isMonthly ? "Monthly SPP" : (pkg.name || pkg.code || "");
          const displayUnitPrice = Number(pkg.price || 0);
          const amountPaid = Number(r.amount_paid || 0);

          return (
            <div
              key={r.id}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                background: "#fff",
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>#{r.id}</div>
                <div>{renderStatusBadge(r.status)}</div>
              </div>

              <div style={{ fontSize: 12, color: "#64748b" }}>User</div>
              <div style={{ fontWeight: 600, wordBreak: "break-all" }}>{userLabel}</div>
              {r.user_full_name ? (
                <div style={{ fontSize: 12, color: "#475569" }}>{r.user_full_name}</div>
              ) : null}

              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>Sponsor</div>
              <div style={{ fontWeight: 600, wordBreak: "break-all" }}>{sponsorLabel || ""}</div>
              {r.sponsor_full_name ? (
                <div style={{ fontSize: 12, color: "#475569" }}>{r.sponsor_full_name}</div>
              ) : null}

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>Role</div>
                <div style={{ fontWeight: 600 }}>{r.user_role_label || "—"}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Pincode</div>
                <div style={{ fontWeight: 600 }}>{r.user_pincode || "—"}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>City</div>
                <div style={{ fontWeight: 600 }}>{r.user_city_name || "—"}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>State</div>
                <div style={{ fontWeight: 600 }}>{r.user_state_name || "—"}</div>
              </div>

              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>Package</div>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>{pkgName}</div>
              <div style={{ fontSize: 12, color: "#475569" }}>
                Type: {pkg.type || ""} • Price: {formatMoney(displayUnitPrice)}
              </div>
              {isMonthly ? (
                <div style={{ fontSize: 12, color: "#475569" }}>
                  Season: {sppSeasonLabel(r)} • Months: {sppMonthsLabel(r)}
                </div>
              ) : null}
              {r.tri_app_slug ? (
                <div style={{ fontSize: 12, color: "#475569" }}>
                  TRI: {r.tri_app_slug}{r.tri_product_id ? ` • Product #${r.tri_product_id}` : ""}
                </div>
              ) : null}
              {r.selected_product_name ? (
                <div style={{ fontSize: 12, color: "#475569" }}>
                  Selected: {r.selected_product_name}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>Qty</div>
                <div style={{ fontWeight: 600 }}>{r.quantity ?? 1}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Amount</div>
                <div style={{ fontWeight: 600 }}>{formatMoney(amountPaid)}</div>
              </div>

              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>Requested</div>
              <div style={{ fontWeight: 600 }}>{requestedAt}</div>

              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>Payment</div>
              <PaymentCell proofUrl={proofUrl} utr={r.utr} remarks={r.remarks} />

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {!readOnly && String(r.status || "").toUpperCase() === "PENDING" ? (
                  <>
                    <button
                      onClick={() => handleApprove(r)}
                      style={{
                        padding: "8px 12px",
                        background: "#059669",
                        color: "#fff",
                        border: 0,
                        borderRadius: 8,
                        cursor: "pointer",
                        flex: 1,
                        fontWeight: 700,
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(r)}
                      style={{
                        padding: "8px 12px",
                        background: "#ef4444",
                        color: "#fff",
                        border: 0,
                        borderRadius: 8,
                        cursor: "pointer",
                        flex: 1,
                        fontWeight: 700,
                      }}
                    >
                      Reject
                    </button>
                  </>
                ) : (
                  <span style={{ color: "#64748b", fontSize: 12 }}>No actions</span>
                )}
              </div>
            </div>
          );
        })}
        {!loading && rows.length === 0 ? (
          <div style={{ padding: 12, color: "#64748b" }}>No results</div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>{title}</h2>
        <div style={{ color: "#64748b", fontSize: 13 }}>
          {description}
        </div>
      </div>

      {/* Optional context line when tri app filter is active (Tri Tour) */}
      {triAppSlug ? (
        <div style={{ marginBottom: 10, color: "#475569", fontSize: 13 }}>
          Filtered by TRI App: <b>{triAppSlug}</b>
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Select
          label="Status"
          value={status}
          onChange={setStatus}
          options={statusOptions}
        />
        <Select
          label="Kind"
          value={kind}
          onChange={setKind}
          options={kindOptions}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "#64748b" }}>User ID</label>
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="numeric id"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              outline: "none",
              background: "#fff",
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "#64748b" }}>From date</label>
          <input
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="YYYY-MM-DD"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              outline: "none",
              background: "#fff",
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "#64748b" }}>To date</label>
          <input
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="YYYY-MM-DD"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              outline: "none",
              background: "#fff",
            }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={fetchRows}
          disabled={loading}
          style={{
            padding: "10px 12px",
            background: "#0f172a",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 700,
          }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
        {err ? <div style={{ color: "#dc2626" }}>{err}</div> : null}
      </div>

      {isMobile ? <MobileCards /> : <DesktopTable />}
    </div>
  );
}
